import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

/**
 * Fill style of the tag.
 *   • tint   – pastel background + matching border (categories)
 *   • dashed – white background + dashed outline (intents, "add" affordances)
 *   • muted  – flat grey (overflow "+n" chips)
 */
export type AiClassificationTagVariant = 'tint' | 'dashed' | 'muted';

/**
 * Pastel tag used across Smart Inbox to render an AI classification —
 * a category, an intent, or an overflow counter.
 *
 * Colour comes from `tone` (a category key such as `rfq`, `order`, …) and is
 * resolved from the `$classification-palette` map in `styles/ui/_colors.scss`.
 * The component renders a `<button>` when `editable` is set and a `<span>`
 * otherwise, so read-only tags stay out of the tab order.
 */
@Component({
  selector: 'ui-ai-classification-tag',
  standalone: true,
  imports: [NgIf, NgTemplateOutlet, SvgIconComponent],
  templateUrl: './ai-classification-tag.component.html',
  styleUrls: ['./ai-classification-tag.component.scss'],
})
export class AiClassificationTagComponent {
  /** Already-translated text to display. */
  @Input() label = '';
  /** Category key driving the colour, e.g. `rfq`. Ignored for muted/dashed. */
  @Input() tone = 'undefined';
  @Input() variant: AiClassificationTagVariant = 'tint';
  @Input() size: 'small' | 'medium' = 'medium';
  /** Makes the tag focusable/clickable (renders a `<button>`). */
  @Input() editable = false;
  /**
   * Whether an editable tag shows a chevron. True for tags that open a menu,
   * false for tags that toggle in place (e.g. the filter panel).
   */
  @Input() caret = true;
  /** Fades the content into a skeleton shimmer while an override is in flight. */
  @Input() loading = false;
  @Input() disabled = false;
  @Input() tooltip = '';

  @Output() tagClick = new EventEmitter<MouseEvent>();

  get hostClasses(): string {
    return [
      'ai-tag',
      `ai-tag--${this.variant}`,
      `ai-tag--${this.size}`,
      this.variant === 'tint' ? `ai-tag--tone-${this.tone}` : '',
      this.editable ? 'ai-tag--editable' : '',
      this.loading ? 'ai-tag--loading' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  onClick(event: MouseEvent): void {
    if (this.disabled || this.loading) return;
    this.tagClick.emit(event);
  }
}
